import { useEffect, useRef, type KeyboardEvent } from 'react'
import { Loader2, SendHorizontal } from 'lucide-react'

interface Props {
  value: string
  onChange: (next: string) => void
  onSend: () => void
  /** Number of in-flight AI requests. Shown as a small spinner indicator
   *  but never blocks sending — user can keep typing/sending more notes. */
  pendingCount: number
  /** Auto-focus the input on mount. */
  autoFocus?: boolean
}

/**
 * Slack-style input: textarea grows up to 6 lines; Enter sends, Shift+Enter
 * inserts a newline. Send button is enabled whenever the input has content,
 * regardless of in-flight AI requests — user can fire off rapid notes and
 * each gets its own thinking bubble + response in the chat log.
 */
export function ChatInput({ value, onChange, onSend, pendingCount, autoFocus }: Props) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)

  // Auto-resize the textarea to fit content (up to 6 lines).
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 6 * 20 + 16) + 'px'
  }, [value])

  useEffect(() => {
    if (autoFocus) textareaRef.current?.focus()
  }, [autoFocus])

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter is a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (value.trim()) onSend()
    }
  }

  const canSend = value.trim().length > 0

  return (
    <div className="border-t border-zinc-200 bg-white p-2">
      <div className="flex items-end gap-2 rounded-lg border border-zinc-300 bg-white px-2 py-1.5 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder="Type a note about the call…  (Enter to send, Shift+Enter for newline)"
          className="flex-1 resize-none border-0 bg-transparent text-[13px] leading-relaxed text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
        />
        <button
          type="button"
          onClick={onSend}
          disabled={!canSend}
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-blue-600 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
          title="Send (Enter)"
        >
          <SendHorizontal className="h-4 w-4" />
        </button>
      </div>
      <div className="mt-1 flex items-center justify-between px-1 text-[10px] text-zinc-400">
        <span>Notes live in browser memory only. Closing the panel discards them.</span>
        {pendingCount > 0 && (
          <span className="flex items-center gap-1 font-medium text-blue-600">
            <Loader2 className="h-3 w-3 animate-spin" />
            {pendingCount} thinking…
          </span>
        )}
      </div>
    </div>
  )
}
